import { motion, AnimatePresence } from 'framer-motion';
import type { SeatPlayer, GamePhase } from '@/types';
import Card from './Card';
import ChipStack from './ChipStack';
import { User, Crown, Target } from 'lucide-react';

interface PlayerSeatProps {
  player: SeatPlayer;
  position: { x: number; y: number };
  isActive: boolean;
  isDealer: boolean;
  gamePhase: GamePhase;
  showCards?: boolean;
  timeLeft?: number;
  maxTime?: number;
}

const POSITION_NAMES: Record<string, string> = {
  'BTN': '庄家',
  'SB': '小盲',
  'BB': '大盲',
  'UTG': '枪口',
  'UTG+1': '枪口+1',
  'MP': '中位',
  'MP+1': '中位+1',
  'CO': ' cutoff',
  'EP': '早位',
  'LP': '晚位',
};

export default function PlayerSeat({ 
  player, 
  position, 
  isActive, 
  isDealer, 
  gamePhase: _gamePhase,
  showCards = false,
  timeLeft,
  maxTime = 15
}: PlayerSeatProps) {
  const isFolded = player.status === 'folded';
  const isAllIn = player.status === 'all-in';
  const isEliminated = player.status === 'eliminated';

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: 1, 
        opacity: isEliminated ? 0.4 : 1,
      }}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
      }}
      className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${
        isActive ? 'z-20' : 'z-10'
      }`}
    >
      {/* 活跃指示器 */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute -inset-3 bg-amber-400/30 rounded-full animate-pulse"
          />
        )}
      </AnimatePresence>

      {/* 倒计时环 - 仅在活跃时显示 */}
      {isActive && timeLeft !== undefined && (
        <>
          {/* 外圈发光效果 */}
          <motion.div
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: timeLeft < 10 ? [0.5, 0.8, 0.5] : 0.3
            }}
            transition={{ 
              duration: timeLeft < 5 ? 0.3 : 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className={`absolute -inset-4 rounded-full blur-md ${
              timeLeft > 8 ? 'bg-emerald-500' : 
              timeLeft > 3 ? 'bg-amber-500' : 
              'bg-red-500'
            }`}
          />
          
          {/* 倒计时圆环 */}
          <div className="absolute -inset-2">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              {/* 背景圆环 */}
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke="#1e293b"
                strokeWidth="6"
              />
              {/* 进度圆环 */}
              <motion.circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke={timeLeft > 8 ? '#10b981' : timeLeft > 3 ? '#f59e0b' : '#ef4444'}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${(timeLeft / maxTime) * 289} 289`}
                initial={{ strokeDasharray: "289 289" }}
                animate={{ strokeDasharray: `${(timeLeft / maxTime) * 289} 289` }}
                transition={{ duration: 0.1, ease: "linear" }}
              />
            </svg>
          </div>

          {/* 倒计时数字 */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: timeLeft < 5 ? [1, 1.2, 1] : 1,
              opacity: 1
            }}
            transition={{ 
              duration: timeLeft < 5 ? 0.2 : 0.2,
              repeat: timeLeft < 5 ? Infinity : 0
            }}
            className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-xs font-bold ${
              timeLeft > 8 ? 'bg-emerald-500 text-white' :
              timeLeft > 3 ? 'bg-amber-500 text-white' :
              'bg-red-500 text-white animate-pulse'
            }`}
          >
            {Math.ceil(timeLeft)}s
          </motion.div>
        </>
      )}

      {/* 玩家卡片 */}
      <div className={`relative bg-slate-800/90 backdrop-blur-sm rounded-xl p-3 border-2 ${
        isActive ? 'border-amber-400 shadow-lg shadow-amber-400/20' : 
        isFolded ? 'border-slate-600 opacity-60' :
        'border-slate-700'
      }`}>
        {/* 位置标签 */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
            player.position === 'BTN' ? 'bg-amber-500 text-white' :
            player.position === 'SB' ? 'bg-blue-500 text-white' :
            player.position === 'BB' ? 'bg-red-500 text-white' :
            'bg-slate-600 text-white'
          }`}>
            {POSITION_NAMES[player.position] || player.position}
          </span>
        </div>

        {/* 庄家按钮 */}
        {isDealer && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
            <span className="text-slate-800 text-xs font-bold">D</span>
          </div>
        )}

        {/* AI 等级标识 */}
        {!player.isHuman && player.aiLevel && (
          <div className="absolute -top-2 -left-2">
            {player.aiLevel === 'hard' ? (
              <Crown className="w-5 h-5 text-amber-400" />
            ) : player.aiLevel === 'medium' ? (
              <Target className="w-5 h-5 text-blue-400" />
            ) : null}
          </div>
        )}

        {/* 头像和名称 */}
        <div className="flex flex-col items-center mb-2">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-1 ${
            player.isHuman ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
            player.aiLevel === 'hard' ? 'bg-gradient-to-br from-red-500 to-red-700' :
            player.aiLevel === 'medium' ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
            'bg-gradient-to-br from-green-500 to-green-700'
          }`}>
            <User className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium truncate max-w-[80px]">
            {player.name}
          </span>
        </div>

        {/* 筹码数 */}
        <div className="text-center mb-2">
          <span className={`font-mono font-bold ${
            isAllIn ? 'text-red-400' : 'text-emerald-400'
          }`}>
            {isAllIn ? 'ALL IN ' : ''}{player.chips.toLocaleString()}
          </span>
        </div>

        {/* 当前下注 */}
        {player.currentBet > 0 && (
          <div className="absolute -top-12 left-1/2 -translate-x-1/2">
            <ChipStack amount={player.currentBet} size="sm" />
          </div>
        )}

        {/* 手牌 */}
        <div className="flex gap-1 justify-center">
          {player.holeCards ? (
            <>
              <Card 
                card={player.holeCards[0]} 
                hidden={!player.isHuman && !showCards}
                size="sm" 
              />
              <Card 
                card={player.holeCards[1]} 
                hidden={!player.isHuman && !showCards}
                size="sm" 
              />
            </>
          ) : (
            <>
              <div className="w-8 h-11 rounded bg-slate-700/50" />
              <div className="w-8 h-11 rounded bg-slate-700/50" />
            </>
          )}
        </div>

        {/* 状态标签 */}
        {(isFolded || isAllIn) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
            <span className={`font-bold text-lg ${
              isFolded ? 'text-slate-400' : 'text-red-400'
            }`}>
              {isFolded ? '弃牌' : 'ALL IN'}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
